import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import NumberFormat from 'react-number-format';
// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import Button from "components/CustomButtons/Button.js";
import { CustomList } from 'components/CustomList/CustomList.jsx';
import { Modal } from 'components/Modal/Modal';
import { tableHeaders } from './config';
import Autocomplete from '@mui/material/Autocomplete';
import { TextField, Chip, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ImageDropzone } from 'components/ImageDropzone/ImageDropzone';
import { setSnackbar } from "redux/actions/search/searchActions";
//actions
import {
  changeStation,
  changeCompany,
  editRowSelected,
  getCategories,
  newExpense,
  checkAvailability,
} from "redux/ducks/expenses";

import { AutocompleteComponent } from "components/Autocomplete/AutocompleteComponent";

function NewExpense({ handleClose }) {
  const dispatch = useDispatch()
  //selectors
  const { companiesList } = useSelector(state => state.companies)
  const categories = useSelector((state) => state.expenses.categories)
  const stationSelected = useSelector((state) => state.expenses.stationSelected)
  const { company, isLoading } = useSelector((state) => state.expenses)
  //local state
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState('')
  const [stations, setStations] = useState([]);
  const [itemModal, setItemModal] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [amount, setAmount] = useState('');
  const [image, setImage] = useState([]);
  const [concept, setConcept] = useState('');
  const [idExpenseCategory, setIdExpenseCategory] = useState('');

  const handleDate = (date) => {
    dispatch(changeCompany([]))
    dispatch(changeStation([]))
    const formatDate = moment(date).format("YYYY-MM-DD")
    setDate(formatDate)
  }

  const fetchCategories = useCallback(() => {
    dispatch(getCategories())
  }, [dispatch])

  const handleCategories = (category) => {
    setIdExpenseCategory(category)
  }

  const handleCompanies = async (_e, company) => {
    dispatch(changeStation([]))
    if (company?.id) {
      const isOpenDate = await dispatch(checkAvailability(date, company));
      if (isOpenDate) {
        dispatch(changeCompany(company))
      }
    }
  }

  const handleChangeStation = (stations) => {
    dispatch(changeStation(stations))
  }

  const onHandleEdit = (row) => {
    setShowModal(true)
    setItemModal(row)
  }

  const onHandleEditItem = (event) => {
    setItemModal({
      ...itemModal,
      percent: event.target.value
    })
  }

  const handleEditPercent = () => {
    dispatch(editRowSelected(itemModal))
    setShowModal(false)
  }

  //limit valueNumber
  const withValueLimit = useCallback(({ floatValue }) => {
    if (floatValue === undefined) return true;
    return floatValue >= -10000000 && floatValue <= 10000000;
  }, []);

  const handleSubmit = () => {
    const sum = stationSelected.reduce((sum, current) => sum + Number(current.percent), 0)
    if (sum && sum > 100) {
      dispatch(setSnackbar(`La suma del porcentaje no puede ser mas del 100%: ${sum}`))
      return
    }
    if (concept.length < 5) {
      dispatch(setSnackbar(`El concepto tiene que contener al menos 5 caracteres ${concept}`))
      return
    }
    if (!amount) {
      dispatch(setSnackbar(`Falta importe`))
      return
    }
    if (!image || image.length === 0) {
      dispatch(setSnackbar(`Es obligatorio adjuntar al menos una imagen`))
      return
    }
    // Validar formato de archivos antes de enviar
    const allowedExtensions = ['.pdf', '.xml', '.jpg', '.jpeg', '.png'];
    for (const file of image) {
      const ext = file.name?.toLowerCase().slice(file.name.lastIndexOf('.'));
      if (!allowedExtensions.includes(ext)) {
        dispatch(setSnackbar(`Formato no permitido: ${file.name}. Solo se aceptan: .pdf, .xml, .jpg, .jpeg, .png`))
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        dispatch(setSnackbar(`El archivo "${file.name}" excede 5MB.`))
        return
      }
    }
    const stations = stationSelected.map((station) => {
      return {
        idStation: station.value,
        percentage: Number(station.percent)
      }
    })

    const idCategory = idExpenseCategory ? idExpenseCategory.value : idExpenseCategory
    const form = {
      date,
      amount: Number(amount),
      concept,
      idExpenseCategory: idCategory,
      stations: stations
    }
    if (form) {
      sendData(form, image)
    }
  }

  const sendData = useCallback(
    (body, image) => {
      dispatch(newExpense(body, image, handleClose));
    },
    [dispatch, handleClose]
  );

  // Effects
  useEffect(() => {
    dispatch(changeCompany([]))
    dispatch(changeStation([]))
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (company?.stations?.length > 0) setStations(company?.stations)
  }, [company])

  useEffect(
    () => {
      if (stationSelected.length === stations.length) {
        const label = "GASTOS ADMINISTRATIVOS";
        const value = categories?.find(c => c.label === label)
        setIdExpenseCategory(value)
      }
    },
    // eslint-disable-next-line
    [stationSelected]
  )

  return (
    <>
        <GridContainer justify="center">
          <GridItem xs={12} sm={4}>
            <TextField
              label="Fecha"
              type="date"
              name="date"
              value={date}
              variant="outlined"
              fullWidth
              onChange={({ target }) => handleDate(target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: today }}
            />
          </GridItem>
          <GridItem xs={12} sm={4}>
            <div style={{ position: "relative", zIndex: 999 }}>
              <Autocomplete
                id="autocomplete-inventory-stations"
                options={companiesList}
                value={company}
                getOptionLabel={(option) =>
                  option.label ? option.label : option.name || ''
                }
                disabled={!date}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option?.name ? option.name : option.label}
                      {...getTagProps({ index })}
                      style={{ borderRadius: 4 }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Empresa"
                    variant="outlined"
                    size="small"
                  />
                )}
                fullWidth
                onChange={(e, v) => handleCompanies(e, v)}
                noOptionsText="No se encuentra la empresa"
                closeIcon={
                  <CloseIcon fontSize="small" style={{ marginRight: 24 }} />
                }
              />
            </div>
          </GridItem>
          <GridItem xs={12} sm={4}>
            <AutocompleteComponent
              label="Plantas"
              name="stations"
              value={stationSelected}
              options={stations}
              multiple
              enableSelectAll
              allLabel="Todas las plantas"
              disabled={!company?.stations}
              size="small"
              limitTags={0}
              getLimitTagsText={(more) => `${more} plantas seleccionadas`}
              renderTags={() => null}
              handleValues={(newValues) => handleChangeStation(newValues.stations || [])}
              textFieldProps={{
                placeholder: stationSelected.length > 0
                  ? `${stationSelected.length} planta(s) seleccionada(s)`
                  : "Seleccionar plantas..."
              }}
            />
          </GridItem>
        </GridContainer>

        <div style={{ margin: '8px auto', width: '100%' }}>
          <CustomList
            rows={stationSelected}
            headers={tableHeaders}
            onHandleClick={onHandleEdit}
          />
        </div>

        <GridContainer alignItems="flex-end">
          <GridItem xs={12} sm={8}>
            <AutocompleteComponent
              label="Categoría"
              name="categoria"
              value={idExpenseCategory || null}
              options={categories}
              size="small"
              handleValues={(newValues) => handleCategories(newValues.categoria)}
            />
          </GridItem>
          <GridItem xs={12} sm={4} style={{ paddingBottom: 2 }}>
            <NumberFormat
              customInput={TextField}
              label="Importe"
              variant="outlined"
              size="small"
              fullWidth
              decimalSeparator={"."}
              fixedDecimalScale={true}
              onValueChange={(values) => {
                const { value } = values;
                setAmount(value);
              }}
              thousandSeparator={true}
              value={amount}
              prefix={'$'}
              allowNegative={true}
              isAllowed={withValueLimit}
            />
          </GridItem>
        </GridContainer>

        <GridContainer style={{ marginTop: 16 }}>
          <GridItem xs={12} sm={12}>
            <TextField
              id="outlined-multiline-static"
              label="Concepto"
              multiline
              rows={4}
              fullWidth
              onChange={(e) => setConcept(e.target.value)}
              value={concept}
              inputProps={{ maxLength: 500 }}
            />
          </GridItem>
          <GridItem xs={12} sm={12} style={{ marginTop: 16 }}>
            <ImageDropzone
              images={image}
              onChange={setImage}
            />
          </GridItem>
        </GridContainer>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button
            color="danger"
            onClick={handleClose}
          >
            Cancelar
          </Button>
          <Button
            color="info"
            onClick={() => handleSubmit()}
            disabled={!stationSelected?.length > 0 || isLoading}
          >
            {isLoading ? 'Guardando...' : 'Guardar'}
          </Button>
        </Box>

      <Modal
        isOpen={showModal}
        textContent={`Editar porcentaje de Planta: ${itemModal ? itemModal.label : ''}`}
        textButton1="Editar"
        textButton2="Cancelar"
        actionFunction2={handleEditPercent}
        handleClose={() => setShowModal(false)}
      >
        <label>Porcentaje: </label>
        <CustomInput
          id="percent"
          formControlProps={{
            fullWidth: true,
          }}
          inputProps={{
            type: "text",
            value: itemModal?.percent,
            onChange: (e) => onHandleEditItem(e)
          }}
        />
      </Modal>
    </>
  )
}

export default NewExpense;
